import * as CommonAnalytics from './CommonAnalyticsBeacons'
import * as BrowseAnalytics from './BrowseAnalyticsBeacons'
import { inject } from '../di'
import { ConfigService } from '../ConfigService'
import {
    initSessionStorageSequenceId,
    getSessionStorageSequenceId,
    increaseSequenceId,
} from '../../utils/SessionStorageSequenceId'

import { AnalyticsService } from '../AnalyticsService'
import { AvroService, Options } from './AvroService'
import { SchemaInfo, schemaNames } from './CommonAnalyticsBeacons'

interface BrowseAvroAnalyticsChannelInterface {
    FireBrowseAdAdaptedOpenAddToCartAd(options: Options): void
    FireBrowseAdAdaptedOpenAddToShoppingListAd(options: Options): void
    FireBrowseAdAdaptedImpressionAd(options: Options): void
    FireBrowseAdAdaptedViewableImpressionAd(options: Options): void
}

export default class BrowseAvroAnalyticsChannel
    implements BrowseAvroAnalyticsChannelInterface
{
    @inject config: ConfigService
    @inject avro: AvroService
    @inject analytics: AnalyticsService

    constructor() {
        initSessionStorageSequenceId()
    }

    public FireBrowseAdAdaptedOpenAddToCartAd(options: Options) {
        this.avro.FireAvroBeacon(this.BrowseAdAdaptedOpenAddToCartAd(options))
    }

    public FireBrowseAdAdaptedOpenAddToShoppingListAd(options: Options) {
        this.avro.FireAvroBeacon(
            this.BrowseAdAdaptedOpenAddToShoppingListAd(options)
        )
    }

    public FireBrowseAdAdaptedImpressionAd(options: Options) {
        this.avro.FireAvroBeacon(this.BrowseAdAdaptedImpressionAd(options))
    }

    public FireBrowseAdAdaptedViewableImpressionAd(options: Options) {
        this.avro.FireAvroBeacon(
            this.BrowseAdAdaptedViewableImpressionAd(options)
        )
    }

    private BrowseAdAdaptedOpenAddToCartAd(
        options: Options
    ): BrowseAnalytics.BrowseBeacon.BrowseAdAdaptedOpenAddToCartAd {
        return this.getCommonFields(
            options,
            BrowseAnalytics.Schemas.BrowseAdAdaptedOpenAddToCartAdSchemaId
        )
    }

    private BrowseAdAdaptedOpenAddToShoppingListAd(
        options: Options
    ): BrowseAnalytics.BrowseBeacon.BrowseAdAdaptedOpenAddToShoppingListAd {
        return {
            listAdAdaptedItems: options.items,
            ...this.getCommonFields(
                options,
                BrowseAnalytics.Schemas
                    .BrowseAdAdaptedOpenAddToShoppingListAdSchemaId
            ),
        }
    }

    private BrowseAdAdaptedImpressionAd(
        options: Options
    ): BrowseAnalytics.BrowseBeacon.BrowseAdAdaptedImpressionAd {
        return this.getCommonFields(
            options,
            BrowseAnalytics.Schemas.BrowseAdAdaptedImpressionAdSchemaId
        )
    }

    private BrowseAdAdaptedViewableImpressionAd(
        options: Options
    ): BrowseAnalytics.BrowseBeacon.BrowseAdAdaptedViewableImpressionAd {
        return this.getCommonFields(
            options,
            BrowseAnalytics.Schemas.BrowseAdAdaptedViewableImpressionAdSchemaId
        )
    }

    private getCommonFields(
        options: Options,
        schemaId: keyof typeof schemaNames
    ) {
        return {
            base: this.avro.getBase(CommonAnalytics.ApplicationID.FlippWeb),
            flippWebBase: this.getFlippWebBase(),
            flippWebBaseExtended: this.getFlippWebBaseExtended(),
            userAccount: {
                'com.flipp.beacon.common.entity.UserAccount':
                    this.getUserAccount(),
            },
            browseContext: this.getBrowseContext(),
            adAdaptedAdContext: this.getAdAdaptedAdContext(options),
            schemaInfo: {
                schemaId,
                schemaName: { string: schemaNames[schemaId] },
            } as SchemaInfo,
        }
    }

    private getFlippWebBase(): BrowseAnalytics.FlippWebBase {
        increaseSequenceId()

        return {
            appVersion: this.config.BUILD,
        }
    }

    private getFlippWebBaseExtended(): BrowseAnalytics.FlippWebBaseExtended {
        increaseSequenceId()

        return {
            environment: this.avro.getEnvironment(),
            clientParameters: this.avro.getClientParameters(),
            locationSource: this.avro.getLocationSource(),
            epochMilliseconds: new Date().getTime(),
            sessionId: this.analytics.sessionId,
            sequenceId: getSessionStorageSequenceId(),
        }
    }

    private getUserAccount(): CommonAnalytics.UserAccount {
        const guid = this.analytics.getAccountGuid()
        return { accountId: guid ? guid : null }
    }

    private getBrowseContext(): BrowseAnalytics.BrowseContext {
        return { pageName: document.title }
    }

    private getAdAdaptedAdContext(
        options: Options
    ): BrowseAnalytics.AdAdaptedAdContext {
        return {
            id: options.id,
            zoneId: options.zoneId,
        }
    }
}
